import * as React from 'react';
import { connect } from 'react-redux';
import DispatchProps from '../../application/core/DispatchProps';
import State from '../../application/core/State';
import { default as getOrderDetails, OrdersDetails } from '../../domain/orders/getOrderDetails';
import OrderPlaceRequested from '../../application/order/events/OrderPlaceRequested';
import OrderDraftSaveRequested from '../../application/order/events/OrderDraftSaveRequested';
import Footer from '../Footer';
import Order, { SumValues } from '../../domain/Order';
import Tooltip from '../tooltip/Tooltip';
import Checkbox from '../common/Checkbox';
import TermsRequested from 'src/application/estimations/events/TermsRequested';
import containsAtLeastOneOrder from '../../domain/estimations/containsAtLeastOneNotSavedOrder';
import formatNumber, { formatPrice } from '../formatNumber';
import { PlacingOderStatus } from '../../application/order/State';

interface StateProps {
    order: Order;
    userDiscount: number;
    deliveryFee: number;
    placingOrderStatus: PlacingOderStatus;
    orderedCheckout: SumValues;
    orderedStock: SumValues;
    estimated: SumValues;
    shippingMethod: string;
}

interface ComponentState {
    checked: boolean;
}

class OrderFooter extends React.PureComponent<StateProps & DispatchProps, ComponentState> {
    constructor(props: StateProps & DispatchProps) {
        super(props);
        this.state = {
            checked: false
        };
    }

    public render(): JSX.Element {
        return (
            <Footer
                class="footer-container-orders"
                details={this.renderOrderDetails()}
                buttons={this.renderOrderButtons()}
                terms={this.renderTerms()}
                hiddenDetails={this.renderHiddenDetails()}
                hasButton={true}
            />
        );
    }

    private renderTerms = (): JSX.Element => {
        const onTermsClick = (): void => this.props.dispatch(new TermsRequested());

        return (
            <div className="footer-terms-wrapper">
                <div>
                    <Checkbox onChange={this.onCheckboxChange} value={this.state.checked} />
                </div>
                <div className="footer-terms-text">
                    I accept
                    <button onClick={onTermsClick}>order terms</button>
                </div>
            </div>
        );
    };

    private renderOrderDetails = (): JSX.Element => {
        const ordersDetails: OrdersDetails = getOrderDetails(this.props.order, this.props.userDiscount);
        return (
            <div className="details-row">
                <span className="details-label">Current order</span>
                {this.renderOrderSum(
                    {
                        total: ordersDetails.checkoutDetails.totalPrice,
                        quantity: ordersDetails.checkoutDetails.pcs
                    },
                    'Checkout'
                )}
                {this.renderOrderSum(
                    {
                        total: ordersDetails.stockDetails.totalPrice,
                        quantity: ordersDetails.stockDetails.pcs
                    },
                    'Stock'
                )}
                {this.props.shippingMethod !== '' && this.renderDeliveryFee(this.props.deliveryFee)}
                {this.renderOrderSum(
                    {
                        total: ordersDetails.total.totalPrice,
                        quantity: ordersDetails.total.pcs
                    },
                    'Total'
                )}
            </div>
        );
    };

    private renderHiddenDetails = (): JSX.Element => {
        return (
            <>
                <div className="footer-hidden">
                    <div className="details-row">
                        <span className="details-label">Ordered</span>
                        {this.renderOrderSum(this.props.orderedCheckout, 'Checkout')}
                        {this.renderOrderSum(this.props.orderedStock, 'Stock')}
                    </div>
                    <div className="details-row">
                        <span className="details-label">Total estimation</span>
                        {this.renderOrderSum(this.props.estimated, '')}
                    </div>
                </div>
            </>
        );
    };

    private renderOrderSum = (values: SumValues, label: string): JSX.Element => (
        <span className="details-wrapper">
            {label !== '' && (
                <span className="details-month details-month-order">
                    <span>{label}:</span>
                </span>
            )}
            <span className="details-calculated">{`${formatNumber(values.quantity, 0)} PCS / € ${formatPrice(Number(values.total))}`}</span>
        </span>
    );

    private renderDeliveryFee = (deliveryFee: number): JSX.Element => (
        <span className="details-wrapper details-small">
            <span className="details-month details-month-order">
                <span>DAP:</span>
            </span>
            <span className="details-calculated">{deliveryFee ? formatPrice(deliveryFee) : 0} €</span>
        </span>
    );

    private renderOrderButtons = (): JSX.Element => {
        const validationStatus = this.getValidationStatus();
        return (
            <div className="buttons-wrapper">
                <Tooltip enabled={validationStatus.message !== ''} message={validationStatus.message}>
                    <button
                        disabled={validationStatus.message !== ''}
                        className={validationStatus.buttonClass}
                        onClick={this.onOrderPlaceRequested}
                    >
                        Place order
                    </button>
                </Tooltip>
                <button className="buttons-draft link-button" onClick={this.onOrderDraftSaveRequested}>
                    Save draft
                </button>
            </div>
        );
    };

    private getValidationStatus = (): { message: string; buttonClass: string } => {
        if (this.props.placingOrderStatus === PlacingOderStatus.Progress) {
            return {
                message: 'Processing...',
                buttonClass: 'buttons-save disabled'
            };
        } else if (!containsAtLeastOneOrder(this.props.order!)) {
            return {
                message: 'Please, provide/update at least one quantity before placing order',
                buttonClass: 'buttons-save disabled'
            };
        }
        if (!this.state.checked) {
            return {
                message: 'Please accept terms',
                buttonClass: 'buttons-save disabled'
            };
        } else {
            return {
                message: '',
                buttonClass: 'buttons-save'
            };
        }
    };

    private onOrderDraftSaveRequested = (): void => {
        this.props.dispatch(new OrderDraftSaveRequested());
    };

    private onOrderPlaceRequested = (): void => {
        this.setState({ checked: false });
        this.props.dispatch(new OrderPlaceRequested());
    };

    private onCheckboxChange = (isChecked: boolean): void => {
        this.setState({ checked: isChecked });
    };
}

const mapStateToProps = (state: State): StateProps => ({
    order: state.order.order!,
    userDiscount: state.user.discountRate,
    deliveryFee: state.order.deliveryFee,
    placingOrderStatus: state.order.placingOrderStatus,
    orderedCheckout: state.order.orderedCheckout,
    orderedStock: state.order.orderedStock,
    estimated: state.order.estimated,
    shippingMethod: state.user.shippingMethod
});

export default connect(mapStateToProps)(OrderFooter);



// WEBPACK FOOTER //
// ./src/view/order/OrderFooter.tsx